import {FC, useEffect, useMemo, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
//mui
import {Box, useMediaQuery} from '@mui/material';
//components
import OrganizationHeader from './OrganizationHeader';
import OrganizationSidebar from './OrganizationSidebar';
//styles
import {templateStyle} from '../Template.styles';
import MobHeader, {MobHeaderContext} from './OrganizationHeader/OrganizationMobHeader';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector, getEvent, getEvents, getEventsSelector} from '@/store/organization-event';
import {useGetOrganizationId, useNotificationSubscription} from '@/utils/hooks';
import {orgStorage} from '@/utils/services/storage.service';
import {STORAGE_KEY, USER_TYPE} from '@/utils/enums';
import {getEventById} from './OrganizationHeader/OrganizationEventSelect/helpers';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {SelectedSectionProvider} from '@/contexts/SelectedSectionContext';
import {useSignalR} from '@/signal/SignalRContext';
import {addNotification, setNotifications} from '@/store/notification';
import * as signalR from '@microsoft/signalr';
import {orgAPI} from '@/core/api';
import {ApiInstanceProvider} from '@/contexts/ApiInstanceContext';
import { getAuctionCategories } from '@/store/organization-auction';

const OrganizationTemplate: FC = () => {
	const [isOpen, setIsOpen] = useState(false);

	const location = useLocation();
	const {connection} = useSignalR();
	useNotificationSubscription(connection, setNotifications, addNotification, 'both');

	const isDashboardRoute = location.pathname.includes('/dashboard');
	const isLaptop = useMediaQuery('(min-width:1200px)');

	const dispatch = useAppDispatch();
	const organizationId = useGetOrganizationId();
	const events = useAppSelector(getEventsSelector);
	const currentEvent = useAppSelector(getCurrentEventSelector);

	useEffect(() => {
		if (!organizationId) return;
		if (events?.length) return;
		const fetchEvents = async () => {
			try {
				await dispatch(getEvents(organizationId)).unwrap();
				await dispatch(getAuctionCategories(orgAPI)).unwrap();

			} catch (error) {
				toast.error(getErrorMessage(error));
			}
		};

		fetchEvents();
	}, [organizationId, dispatch]);

	const values = useMemo(() => ({isOpen, setIsOpen}), [isOpen]);
	const whitePages = ['/purchase-tickets'].some((path) => location.pathname.includes(path));

	useEffect(() => {
		const eventId = orgStorage.get(STORAGE_KEY.ORGANIZATION_EVENT_ID) as string;

		if (!events?.length) return;

		const eventToSet = eventId ? getEventById(events, eventId) : events[0];

		if (eventToSet && eventToSet.id !== currentEvent?.id) {
			dispatch(getEvent(eventToSet.id));
		}
	}, [currentEvent, events, dispatch]);

	useEffect(() => {
		const startConnection = async () => {
			if (connection && connection.state === signalR.HubConnectionState.Disconnected) {
				try {
					await connection.start();
				} catch (err) {
					// console.error('SignalR connection failed: ', err);
				}
			}
		};

		startConnection();

		return () => {
			if (connection && connection.state === signalR.HubConnectionState.Connected) {
				connection.stop();
			}
		};
	}, [connection]);

	return (
		<ApiInstanceProvider value={{apiInstance: orgAPI, role: USER_TYPE.ORGANIZATION}}>
			<MobHeaderContext.Provider value={values}>
				<Box sx={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
					{isDashboardRoute || isLaptop ? <OrganizationHeader /> : <MobHeader />}
					<Box
						sx={{
							...templateStyle.wrapper,
							background: whitePages ? 'white' : 'var(--neutral-50)',
						}}
					>
						<Box sx={templateStyle.content}>
							{isLaptop && <OrganizationSidebar />}

							<Box sx={templateStyle.main}>
								<SelectedSectionProvider>
									<Outlet />
								</SelectedSectionProvider>
							</Box>
						</Box>
					</Box>
				</Box>
			</MobHeaderContext.Provider>
		</ApiInstanceProvider>
	);
};

export default OrganizationTemplate;
